import React, { useMemo } from "react";
import { z } from "zod";
import { FormikProvider, useFormik } from "formik";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { getAvailableAccountTypes } from "@common/constants/domains/accounting/accountTypeHelpers";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { validateFormikWithZodSchema } from "@helpers/form";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { AccountTypeSelectField } from "@components/forms/AccountTypeSelectField/AccountTypeSelectField";
import { GroupSelectField } from "@components/forms/GroupSelectField/GroupSelectField";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { HStack } from "@design-system/HStack/HStack";
import { DraftGroup } from "../draftState";
import { AddFanOutTarget } from "./nestedAddScope";
import {
    NestedFooterAddSelection,
    NestedFooterCreateAccountParams,
} from "./nestedFooterAddTypes";
import styles from "./NestedFooterAddModal.module.scss";

export interface NestedAddAccountModalProps {
    isOpen: boolean;
    // Unused; kept for parity with the group modal contract.
    groups: DraftGroup[];
    // Keyed by row id, not a draft id.
    pickerGroups: AccountGroup[];
    // Unused: accounts have no depth or name-uniqueness gate. Kept for parity.
    resolveFanOutTargets: (
        selection: NestedFooterAddSelection,
    ) => AddFanOutTarget[];
    // Where the picker defaults: the active scope at the moment the footer CTA
    // was triggered. Null only while the modal is closed.
    defaultSelection: NestedFooterAddSelection | null;
    onCancel: () => void;
    onCreate: (params: NestedFooterCreateAccountParams) => void;
}

interface AddAccountFormValues {
    type: AccountType;
    parentRowId: string | null;
    name: string;
}

const addAccountFormSchema = z.object({
    type: z.nativeEnum(AccountType),
    parentRowId: z.string().nullable(),
    name: z.string().trim().min(1),
});

export const NestedAddAccountModal: React.FC<NestedAddAccountModalProps> = ({
    isOpen,
    pickerGroups,
    defaultSelection,
    onCancel,
    onCreate,
}) => {
    const accountTypeOptions = useMemo(() => getAvailableAccountTypes(), []);

    const formik = useFormik<AddAccountFormValues>({
        initialValues: {
            type: defaultSelection?.type ?? AccountType.operatingExpenses,
            parentRowId: defaultSelection?.parentRowId ?? null,
            name: "",
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(addAccountFormSchema),
        onSubmit: (values) => {
            onCreate({
                type: values.type,
                parentRowId: values.parentRowId,
                name: values.name.trim(),
            });
        },
    });

    const canConfirm = formik.values.name.trim().length > 0;

    return (
        <StandardModal
            show={isOpen}
            onHide={onCancel}
            contentClassName={styles.modalContent}
        >
            <StandardModalHeader>Add account</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit}>
                        <AccountTypeSelectField
                            name="type"
                            label="Type"
                            placeholder="Select a type"
                            value={formik.values.type}
                            types={accountTypeOptions}
                            onChange={(type) => {
                                formik.setFieldValue("type", type);
                                formik.setFieldValue("parentRowId", null);
                            }}
                        />
                        <GroupSelectField
                            parentGroupFieldName="parentRowId"
                            label="Group"
                            placeholder="Select a group"
                            groups={pickerGroups}
                            type={formik.values.type}
                            value={formik.values.parentRowId}
                            onChange={(parentRowId) =>
                                formik.setFieldValue("parentRowId", parentRowId)
                            }
                        />

                        <Field className={styles.nameField} label="Name">
                            <Input
                                autoFocus
                                type="text"
                                placeholder="Enter account name"
                                size="xl"
                                value={formik.values.name}
                                onChange={(event) => {
                                    void formik.setFieldValue(
                                        "name",
                                        event.target.value,
                                    );
                                }}
                            />
                        </Field>

                        <HStack
                            as="footer"
                            gap={12}
                            justify="end"
                            className="mt-4"
                        >
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                loading={false}
                                type="submit"
                                disabled={!canConfirm}
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Add account",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                            >
                                Create
                            </ButtonWithLoader>
                        </HStack>
                    </form>
                </FormikProvider>
            </StandardModalBody>
        </StandardModal>
    );
};
